import { Card, Col, Row, ProgressBar, Button, Table } from 'react-bootstrap';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useState } from 'react';
import Avatar from 'components/common/Avatar';
import Flex from 'components/common/Flex';
import SoftBadge from 'components/common/SoftBadge';
import { weUserImage } from 'data/weraise-data/donor-data';
import paths from 'routes/paths';
import { formatMoney } from 'helpers/utils';
import ConfirmationModal from 'components/common/modals/ConfirmationModal';
import { useBeneficiaryContext } from 'context/beneficiaryContext';
import { permissionsKeyMap } from 'routes/permissions/permissionKeys';
import {
  useSponsorshipDetail,
  useEndSponsorship,
  useChangeSponsorshipPayment
} from 'hooks/useBeneficiaries';
import { useAuthContext } from 'context/authContext';
import SelectDonationModal from '../sponsorships/sponsorship-form/SelectDonationModal';
import { format, addMonths } from 'date-fns';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IconButton from 'components/common/IconButton';
import LoadingContainer from 'components/common/LoadingContainer';

/* -------------------------------------------------------------------------- */

const Donors = () => {
  const navigate = useNavigate();
  const [showEndModal, setShowEndModal] = useState(false);
  const [selectedSponsorshipId, setSelectedSponsorshipId] = useState(null);
  const { beneficiary } = useBeneficiaryContext();
  const { id } = useParams();

  const { sponsorshipDetail, isLoading, refetch } = useSponsorshipDetail(id);
  const { endSponsorship, isEnding } = useEndSponsorship();
  const { changePayment } = useChangeSponsorshipPayment();
  const { user } = useAuthContext();
  const [changePaymentFor, setChangePaymentFor] = useState(null);

  const handleChangePaymentSubmit = donation => {
    changePayment(
      {
        sponsorshipId: changePaymentFor,
        newOrderDetailId: donation.orderDetailId,
        actor: {
          userId: user?.id,
          userName:
            `${user?.first_name || ''} ${user?.last_name || ''}`.trim() ||
            user?.email
        }
      },
      { onSuccess: () => refetch() }
    );
  };

  const handleEndClick = sponsorshipId => {
    setSelectedSponsorshipId(sponsorshipId);
    setShowEndModal(true);
  };

  const handleEndModalHide = () => {
    setSelectedSponsorshipId(null);
    setShowEndModal(false);
  };

  const handleConfirmEnd = () => {
    endSponsorship(selectedSponsorshipId, {
      onSuccess: () => {
        handleEndModalHide();
        refetch();
      }
    });
  };

  // Go straight to the sponsorship form for this beneficiary. The donor is
  // chosen per-donation inside the Select Donation step (a single sponsorship
  // can be split across several donors by amount), so no upfront donor picker
  // is needed.
  const handleAddSponsor = () => {
    navigate(`${paths.createNewSponsorship}?beneficiaryId=${beneficiary.id}`);
  };

  if (isLoading) {
    return <LoadingContainer />;
  }

  const sponsorshipsByType = sponsorshipDetail?.sponsorshipsByType || {};
  const sponsorshipTypes = Object.values(sponsorshipsByType);

  // Calculate overall coverage
  const totalMonthlyNeeded = sponsorshipTypes.reduce(
    (sum, t) => sum + (t.sponsorshipType?.cost || 0),
    0
  );
  const totalMonthlyCovered = sponsorshipTypes.reduce(
    (sum, t) => sum + (t.monthlyAmountCovered || 0),
    0
  );
  const overallCoverage =
    totalMonthlyNeeded > 0
      ? Math.round((totalMonthlyCovered / totalMonthlyNeeded) * 100)
      : 0;

  // Get all active sponsors count
  const allActiveSponsors = sponsorshipTypes.flatMap(
    t => t.activeSponsorships || []
  );
  const uniqueDonorIds = [...new Set(allActiveSponsors.map(s => s.donor?.id))];

  // Ended/cancelled sponsorships across all types — retained as history so
  // staff can see who used to sponsor this beneficiary after a cancellation.
  const endedSponsorships = sponsorshipTypes.flatMap(t =>
    (t.sponsorships || [])
      .filter(s => s.status !== 'active')
      .map(s => ({
        ...s,
        typeName: t.sponsorshipType?.sponsorship_type?.type || 'Sponsorship',
        typeCost: t.sponsorshipType?.cost || 0
      }))
  );

  return (
    <>
      <ConfirmationModal
        show={showEndModal}
        onHide={handleEndModalHide}
        loading={isEnding}
        title="End Sponsorship"
        handleConfirm={handleConfirmEnd}
        body="Are you sure you want to end this sponsorship? This action cannot be undone."
      />

      <SelectDonationModal
        show={!!changePaymentFor}
        onHide={() => setChangePaymentFor(null)}
        onSubmit={handleChangePaymentSubmit}
      />

      {/* Summary Section */}
      <Card className="mb-3">
        <Card.Body>
          <Flex justifyContent="between" alignItems="center" className="mb-3">
            <div>
              <h5 className="mb-1">Sponsorship Overview</h5>
              <p className="text-muted mb-0 fs--1">
                {uniqueDonorIds.length} donor
                {uniqueDonorIds.length !== 1 ? 's' : ''} sponsoring this
                beneficiary
              </p>
            </div>
            <IconButton
              icon="plus"
              variant="primary"
              size="sm"
              onClick={handleAddSponsor}
            >
              Add Sponsor
            </IconButton>
          </Flex>

          <Row className="g-3">
            <Col xs={12} md={4}>
              <div className="border rounded p-3 text-center">
                <h6 className="text-muted mb-1">Monthly Cost</h6>
                <h4 className="text-primary mb-0">
                  {formatMoney(totalMonthlyNeeded)}
                </h4>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="border rounded p-3 text-center">
                <h6 className="text-muted mb-1">Amount Covered</h6>
                <h4 className="text-success mb-0">
                  {formatMoney(totalMonthlyCovered)}
                </h4>
              </div>
            </Col>
            <Col xs={12} md={4}>
              <div className="border rounded p-3 text-center">
                <h6 className="text-muted mb-1">Coverage</h6>
                <h4
                  className={`mb-0 ${
                    overallCoverage >= 100
                      ? 'text-success'
                      : overallCoverage > 0
                      ? 'text-warning'
                      : 'text-danger'
                  }`}
                >
                  {overallCoverage}%
                </h4>
              </div>
            </Col>
          </Row>

          <div className="mt-3">
            <ProgressBar style={{ height: '12px' }}>
              <ProgressBar
                variant={
                  overallCoverage >= 100
                    ? 'success'
                    : overallCoverage > 0
                    ? 'warning'
                    : 'danger'
                }
                now={overallCoverage}
              />
            </ProgressBar>
            <div className="d-flex justify-content-between mt-1">
              <small className="text-muted">0%</small>
              <small
                className={
                  overallCoverage >= 100
                    ? 'text-success fw-bold'
                    : overallCoverage > 0
                    ? 'text-warning'
                    : 'text-muted'
                }
              >
                {overallCoverage >= 100
                  ? 'Fully Sponsored'
                  : `${overallCoverage}% Sponsored`}
              </small>
              <small className="text-muted">100%</small>
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Sponsorship Breakdown by Type */}
      {sponsorshipTypes.length > 0 ? (
        sponsorshipTypes.map(typeData => {
          const {
            sponsorshipType,
            activeSponsorships,
            totalCoverage,
            monthlyAmountCovered
          } = typeData;

          const typeName =
            sponsorshipType?.sponsorship_type?.type || 'Sponsorship';
          const typeCost = sponsorshipType?.cost || 0;

          return (
            <Card className="mb-3" key={sponsorshipType?.id}>
              <Card.Header className="bg-light">
                <Flex justifyContent="between" alignItems="center">
                  <div>
                    <h6 className="mb-0">{typeName}</h6>
                    <small className="text-muted">
                      {formatMoney(typeCost)}/month
                    </small>
                  </div>
                  <div>
                    {totalCoverage >= 100 ? (
                      <SoftBadge bg="success" pill>
                        Fully Sponsored
                      </SoftBadge>
                    ) : totalCoverage > 0 ? (
                      <SoftBadge bg="warning" pill>
                        {totalCoverage}% Sponsored
                      </SoftBadge>
                    ) : (
                      <SoftBadge bg="danger" pill>
                        Unsponsored
                      </SoftBadge>
                    )}
                  </div>
                </Flex>
              </Card.Header>
              <Card.Body>
                {/* Coverage Progress */}
                <ProgressBar className="mb-3" style={{ height: '8px' }}>
                  <ProgressBar
                    variant={totalCoverage >= 100 ? 'success' : 'warning'}
                    now={totalCoverage}
                  />
                </ProgressBar>

                {/* Donors Table */}
                {activeSponsorships && activeSponsorships.length > 0 ? (
                  <Table responsive hover size="sm" className="mb-0">
                    <thead>
                      <tr>
                        <th>Donor</th>
                        <th>Coverage</th>
                        <th>Monthly Amount</th>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Duration</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeSponsorships.map(sponsorship => {
                        const isMonthly =
                          sponsorship.order_detail?.billing_condition ===
                          'monthly';
                        const endDate =
                          !isMonthly &&
                          sponsorship.start_date &&
                          sponsorship.duration
                            ? addMonths(
                                new Date(sponsorship.start_date),
                                sponsorship.duration
                              )
                            : null;

                        const donorName = `${
                          sponsorship.donor?.first_name || ''
                        } ${sponsorship.donor?.last_name || ''}`.trim();

                        const monthlyAmount =
                          (typeCost * (sponsorship.coverage_percentage || 0)) /
                          100;

                        return (
                          <tr key={sponsorship.id}>
                            <td>
                              <Flex alignItems="center">
                                <Avatar
                                  src={weUserImage}
                                  size="s"
                                  className="me-2"
                                />
                                {sponsorship.donor?.id ? (
                                  <Link
                                    to={`/donors/${sponsorship.donor.id}`}
                                    className="fw-medium text-primary"
                                  >
                                    {donorName || 'Unknown Donor'}
                                  </Link>
                                ) : (
                                  <span
                                    className="fw-medium text-600"
                                    title="Managed by main branch"
                                  >
                                    External donor
                                  </span>
                                )}
                              </Flex>
                            </td>
                            <td>
                              <SoftBadge
                                bg={
                                  sponsorship.coverage_percentage >= 100
                                    ? 'success'
                                    : 'info'
                                }
                              >
                                {sponsorship.coverage_percentage}%
                              </SoftBadge>
                            </td>
                            <td className="fw-medium">
                              {formatMoney(monthlyAmount)}
                            </td>
                            <td>
                              {sponsorship.start_date
                                ? format(
                                    new Date(sponsorship.start_date),
                                    'dd MMM yyyy'
                                  )
                                : '-'}
                            </td>
                            <td>
                              {isMonthly ? (
                                <SoftBadge bg="success" pill>
                                  Ongoing
                                </SoftBadge>
                              ) : endDate ? (
                                format(endDate, 'dd MMM yyyy')
                              ) : (
                                '-'
                              )}
                            </td>
                            <td>
                              {sponsorship.duration
                                ? `${sponsorship.duration} month${
                                    sponsorship.duration > 1 ? 's' : ''
                                  }`
                                : '-'}
                            </td>
                            <td className="text-end">
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 me-3"
                                onClick={() =>
                                  setChangePaymentFor(sponsorship.id)
                                }
                              >
                                <FontAwesomeIcon icon="exchange-alt" /> Change
                                payment
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                className="text-danger p-0"
                                onClick={() => handleEndClick(sponsorship.id)}
                              >
                                <FontAwesomeIcon icon="times" /> End
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className="bg-light">
                      <tr>
                        <td className="fw-bold">Total</td>
                        <td>
                          <SoftBadge
                            bg={totalCoverage >= 100 ? 'success' : 'warning'}
                          >
                            {totalCoverage}%
                          </SoftBadge>
                        </td>
                        <td className="fw-bold">
                          {formatMoney(monthlyAmountCovered)}
                        </td>
                        <td colSpan={4}></td>
                      </tr>
                    </tfoot>
                  </Table>
                ) : (
                  <div className="text-center py-3">
                    <p className="text-muted mb-2">
                      No active sponsors for this type
                    </p>
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={handleAddSponsor}
                    >
                      <FontAwesomeIcon icon="plus" className="me-1" />
                      Add Sponsor
                    </Button>
                  </div>
                )}
              </Card.Body>
            </Card>
          );
        })
      ) : (
        <Card>
          <Card.Body className="text-center py-5">
            <FontAwesomeIcon
              icon="hand-holding-heart"
              className="text-muted fs-3 mb-3"
            />
            <h5 className="text-muted">No Sponsorship Types Assigned</h5>
            <p className="text-muted mb-3">
              This beneficiary doesn't have any sponsorship types configured
              yet.
            </p>
          </Card.Body>
        </Card>
      )}

      {/* Sponsorship History (ended/cancelled) */}
      {endedSponsorships.length > 0 && (
        <Card className="mb-3 mt-4">
          <Card.Header className="bg-light py-2">
            <h6 className="mb-0 text-600">
              <FontAwesomeIcon icon="clock" className="me-2" />
              Sponsorship History ({endedSponsorships.length})
            </h6>
          </Card.Header>
          <Card.Body>
            <Table responsive size="sm" className="mb-0">
              <thead>
                <tr>
                  <th>Donor</th>
                  <th>Type</th>
                  <th>Coverage</th>
                  <th>Monthly Amount</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {endedSponsorships.map(sponsorship => {
                  const endDate =
                    sponsorship.start_date && sponsorship.duration
                      ? addMonths(
                          new Date(sponsorship.start_date),
                          sponsorship.duration
                        )
                      : null;
                  const donorName = `${sponsorship.donor?.first_name || ''} ${
                    sponsorship.donor?.last_name || ''
                  }`.trim();
                  const monthlyAmount =
                    (sponsorship.typeCost *
                      (sponsorship.coverage_percentage || 0)) /
                    100;

                  return (
                    <tr key={sponsorship.id} className="text-600">
                      <td>
                        {sponsorship.donor?.id ? (
                          <Link
                            to={`/donors/${sponsorship.donor.id}`}
                            className="fw-medium"
                          >
                            {donorName || 'Unknown Donor'}
                          </Link>
                        ) : (
                          <span title="Managed by main branch">
                            {donorName || 'External donor'}
                          </span>
                        )}
                      </td>
                      <td>{sponsorship.typeName}</td>
                      <td>{sponsorship.coverage_percentage}%</td>
                      <td>{formatMoney(monthlyAmount)}</td>
                      <td>
                        {sponsorship.start_date
                          ? format(
                              new Date(sponsorship.start_date),
                              'dd MMM yyyy'
                            )
                          : '-'}
                      </td>
                      <td>{endDate ? format(endDate, 'dd MMM yyyy') : '-'}</td>
                      <td>
                        <SoftBadge
                          bg="secondary"
                          pill
                          className="text-capitalize"
                        >
                          {sponsorship.status}
                        </SoftBadge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default Donors;
